import React, { useCallback, useEffect } from "react";
import { useQuery } from "@apollo/client";
import { Select, Form } from "antd";
import { SelectValue } from "antd/lib/select";
import { FormInstance } from "antd/lib/form";
import { useTranslation } from "react-i18next";

// graphql
import GET_LANGUAGES from "graphql/queries/GetLanguages";

// common
import { ModalLayout } from "common/modal";
import { CheckboxGroup, CustomCheckbox } from "common/checkbox";

// styling
import { Language } from "common/class_card/ClassCardStyle";
import { LinkedText, FormCard, CardTitle } from "./RegistrationStyle";
import { CustomInput, CustomSelect, HskMessage } from "./RegistrationFormStyle";

const { Option } = Select;
const { ModalRow, ModalCol } = ModalLayout;

interface Level {
  id: string;
  title: string;
}
interface Language {
  id: string;
  englishName: string;
  realName: string;
  levels: Level;
}

interface AdLanguage {
  englishName: string;
  languageTail: string;
}
type RegistrationFormProps = {
  form: FormInstance;
  currentLanguageId: string | undefined;
  setCurrentLanguageId: (value: string) => void;
};

// ARRAY TO STORE LANGUAGES FOR WHICH THERE ARE TARGETTED LINKS
const adLinkLanguages = [
  { englishName: "Vietnamese", languageTail: "vn" },
  { englishName: "Mandarin", languageTail: "mn" },
  { englishName: "Shanghainese", languageTail: "sh" },
  { englishName: "Korean", languageTail: "ko" },
  { englishName: "Japanese", languageTail: "jp" },
];

const RegistrationForm = ({
  currentLanguageId,
  setCurrentLanguageId,
  ...props
}: RegistrationFormProps) => {
  const { data: languagesData, loading: languagesLoading } =
    useQuery(GET_LANGUAGES);
  const { t, i18n } = useTranslation("registration");

  const { form } = props;
  const currentURLPathname = window.location.pathname;

  const detectedTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const getLanguageID = useCallback(
    (languageName: string): string => {
      const studyLanguage = languagesData?.languages.find(
        (language: { englishName: string }) => {
          return language.englishName === languageName;
        }
      );
      return studyLanguage?.id;
    },
    [languagesData]
  );

  // function to handle based on language to hide the characters option
  // and determine list of levels
  const switchFunction = useCallback(
    (adLanguage: AdLanguage) => {
      const adLink = `/register/${adLanguage?.languageTail}`;
      const languageName: string = adLanguage?.englishName;
      const languageID: string = getLanguageID(languageName);

      if (adLink === currentURLPathname) {
        setCurrentLanguageId(getLanguageID(languageName));
        form.setFieldsValue({
          currentStudyLanguage: languageID,
        });
      }
    },
    // eslint-disable-next-line
    [currentURLPathname, form, getLanguageID]
  );

  const onLanguageFieldChange = (value: string) => {
    // determines what levels to output based on langauge
    setCurrentLanguageId(value);
    // reset current level ID every time something new is changed
    form.setFieldsValue({
      currentLevelId: undefined,
    });
  };
  useEffect(() => {
    if (!languagesLoading && currentURLPathname && !currentLanguageId) {
      adLinkLanguages.map((adLanguage: AdLanguage) =>
        switchFunction(adLanguage)
      );
    }
  }, [
    currentURLPathname,
    languagesLoading,
    form,
    currentLanguageId,
    getLanguageID,
    switchFunction,
  ]);

  const studyLanguage = languagesData?.languages.find(
    (language: { id: string }) => {
      return language.id === currentLanguageId;
    }
  );

  const checkIfMandarin = () => {
    return studyLanguage?.englishName === "Mandarin";
  };
  const isChinese = () => {
    return studyLanguage?.languageCategory?.name
      .toUpperCase()
      .includes("CHINESE");
  };
  return (
    <FormCard>
      <CardTitle>{t("Register new account").toUpperCase()}</CardTitle>
      <Form.Item
        name="locale"
        hidden
        noStyle
        initialValue={i18n.language}
        style={{ display: "none" }}
      >
        <CustomInput type="hidden" name="locale" />
      </Form.Item>
      <Form.Item
        name="timeZone"
        hidden
        noStyle
        initialValue={detectedTimezone}
        style={{ display: "none" }}
      >
        <CustomInput type="hidden" name="timeZone" />
      </Form.Item>
      <ModalRow gutter={20}>
        <ModalCol xs={24} sm={24} md={12}>
          <Form.Item
            name="givenName"
            rules={[{ required: true, message: t("Given name is required.") }]}
          >
            <CustomInput
              type="text"
              placeholder={`*${t("Given name")}`}
              name="givenName"
              size="large"
            />
          </Form.Item>
        </ModalCol>
        <ModalCol xs={24} sm={24} md={12}>
          <Form.Item
            name="familyName"
            rules={[{ required: true, message: t("Family name is required.") }]}
          >
            <CustomInput
              type="text"
              placeholder={`*${t("Family name")}`}
              name="familyName"
              size="large"
            />
          </Form.Item>
        </ModalCol>
      </ModalRow>

      <ModalRow>
        <ModalCol xs={24}>
          <Form.Item
            name="email"
            rules={[{ required: true, message: t("Email is required.") }]}
          >
            <CustomInput
              type="email"
              placeholder={`*${t("Email")}`}
              name="email"
              size="large"
            />
          </Form.Item>
        </ModalCol>
      </ModalRow>

      <ModalRow gutter={20}>
        <ModalCol xs={24} sm={24} md={12}>
          <Form.Item
            name="password"
            rules={[{ required: true, message: t("Password is required.") }]}
          >
            <CustomInput
              type="password"
              placeholder={`*${t("Password")}`}
              name="password"
              size="large"
            />
          </Form.Item>
        </ModalCol>
        <ModalCol xs={24} sm={24} md={12}>
          <Form.Item
            name="confirmPassword"
            rules={[
              {
                required: true,
                message: t("Password confirmation is required."),
              },
              ({ getFieldValue }) => ({
                validator(rule, value) {
                  if (!value || getFieldValue("password") === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(
                    new Error(
                      t("The two passwords that you entered do not match!")
                    )
                  );
                },
              }),
            ]}
          >
            <CustomInput
              type="password"
              placeholder={`*${t("Confirm password")}`}
              name="confirmPassword"
              size="large"
            />
          </Form.Item>
        </ModalCol>
      </ModalRow>

      <ModalRow>
        <ModalCol xs={24}>
          <Form.Item
            name="currentStudyLanguage"
            rules={[
              {
                required: true,
                message: t("Your study language is required."),
              },
            ]}
            initialValue={currentLanguageId}
          >
            <CustomSelect
              showSearch
              placeholder={t("Choose Language")}
              optionFilterProp="children"
              filterOption={(input: any, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              getPopupContainer={(trigger) => trigger.parentNode}
              onChange={(value: SelectValue) => {
                onLanguageFieldChange(value as string);
              }}
            >
              {languagesData?.languages.map((languages: Language) => (
                <Option value={languages.id} key={languages.id}>
                  {`${languages.englishName} ${languages.realName}`}
                </Option>
              ))}
            </CustomSelect>
          </Form.Item>
        </ModalCol>
      </ModalRow>

      <ModalRow>
        <ModalCol xs={24}>
          <Form.Item
            name="currentLevelId"
            rules={[
              {
                required: true,
                message: t("Your Language level is required."),
              },
            ]}
            shouldUpdate
          >
            <CustomSelect
              showSearch
              placeholder={t("My Language Level")}
              optionFilterProp="children"
              filterOption={(input: any, option: any) =>
                option.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
              }
              getPopupContainer={(trigger) => trigger.parentNode}
            >
              {studyLanguage?.levels.map((level: Level) => (
                <Option value={level.id} key={level.id}>
                  {level.title}
                </Option>
              ))}
            </CustomSelect>
          </Form.Item>
        </ModalCol>
      </ModalRow>

      {checkIfMandarin() && (
        <HskMessage>
          <span>{t("Not sure?")} </span>
          <LinkedText
            href="https://ltl-school.com/free-hsk-test/"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t("Test your level here")}
          </LinkedText>
        </HskMessage>
      )}

      {isChinese() && (
        <ModalRow>
          <ModalCol xs={24}>
            <Form.Item name="charactersPreference">
              <CustomSelect placeholder={t("I want to learn...")}>
                <Option value="simplified" key="simplified">
                  {t("I want to learn Simplified Characters")}
                </Option>
                <Option value="traditional" key="traditional">
                  {t("I want to learn Traditional Characters")}
                </Option>
              </CustomSelect>
            </Form.Item>
          </ModalCol>
        </ModalRow>
      )}
      <CheckboxGroup>
        {isChinese() && (
          <Form.Item name="spokenOnly" valuePropName="checked">
            <CustomCheckbox name="spokenOnly">
              {t("I study only spoken Chinese (no characters)")}
            </CustomCheckbox>
          </Form.Item>
        )}
        <Form.Item name="sendTips" valuePropName="checked" initialValue>
          <CustomCheckbox name="sendTips">
            {t("Send me tips, updates, and offers to help my study")}
          </CustomCheckbox>
        </Form.Item>
        <Form.Item
          name="agreedPrivacyPolicy"
          rules={[
            {
              // For checkbox, have to do this kind of validation.
              // Cannot simply use required:true like for input, will lead to inconsistency.
              validator: (_, value) =>
                value
                  ? Promise.resolve()
                  : Promise.reject(
                      new Error(
                        t("Agreement to LTL's Privacy Policy is required.")
                      )
                    ),
            },
          ]}
          valuePropName="checked"
        >
          <CustomCheckbox name="agreedPrivacyPolicy">
            {t("I agree to the")}
            <LinkedText
              className="linked-text"
              tabIndex={0}
              href={t("PRIVACY_POLICY_URL")}
              target="_blank"
              rel="noopener noreferrer"
            >
              {" "}
              {t("Privacy Policy")}
            </LinkedText>
          </CustomCheckbox>
        </Form.Item>
      </CheckboxGroup>
    </FormCard>
  );
};

export default RegistrationForm;
